import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faUsers, faEdit } from "@fortawesome/free-solid-svg-icons";
import DateFormat from "../ui/DateFormat";

import { DEFAULT_AVATAR } from "../../utils/image";
const AVATAR_FALLBACK =
  DEFAULT_AVATAR;

const ScorePicker = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) => {
  const [hovered, setHovered] = useState(0);
  // Ten hard-edged score plates; the run up to the hovered/picked one lights
  // in signal blue.
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label="Score from 1 to 10">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          aria-label={`Score ${n}`}
          aria-pressed={n === value}
          className={`flex h-9 w-9 items-center justify-center font-display text-base font-semibold tabular-nums transition-colors duration-150 ${
            n <= (hovered || value)
              ? "bg-signal text-white"
              : "bg-theme-surface text-theme-text-secondary hover:text-theme-text"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );
};

const CommunityRating = ({ gameId }: { gameId: string }) => {
  const { data: session } = useSession();
  const [data, setData] = useState<{
    average: number | null;
    total: number;
    ratings: any[];
  }>({ average: null, total: 0, ratings: [] });
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  const fetchRatings = async () => {
    try {
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/games/${gameId}/rating`
      );
      const json = await res.json();
      if (json.success) setData(json.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [gameId]);

  // Pre-fill if user already rated
  useEffect(() => {
    if (!session || data.ratings.length === 0) return;
    const mine = data.ratings.find(
      (r) => r.userId?._id === (session.user as any)?.id
    );
    if (mine) {
      setScore(mine.score);
      setComment(mine.comment || "");
      setSubmitted(true);
    }
  }, [session, data.ratings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (score === 0) {
      setError("Please select a score from 1 to 10.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/games/${gameId}/rating`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(session?.user as any)?.accessToken}`,
          },
          body: JSON.stringify({ score, comment }),
        }
      );
      const json = await res.json();
      if (json.success) {
        setSubmitted(true);
        setShowForm(false);
        await fetchRatings();
      } else {
        setError("Failed to submit. Please try again.");
      }
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // One score rule site-wide (ArticleCard, Top 10): 9, 9.5, 9.75. (The old
  // green/yellow/red ink compiled to nothing for yellow: `yellow` is one colour.)
  const formatScore = (s: number) => String(Math.round(s * 100) / 100);

  return (
    <div className="mt-8">
      {/* Header */}
      <h2 className="seg-title mb-4">Community Rating</h2>

      {/* Aggregate: the score bug, the count, a ten-step meter, the action */}
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-4 rounded-[4px] bg-theme-card px-4 py-4 md:px-6">
        <div className="flex items-stretch">
          <span
            className={`score-bug h-14 min-w-[4.5rem] text-4xl ${
              data.average !== null ? "" : "!bg-theme-surface !text-theme-text-secondary"
            }`}
          >
            {data.average !== null ? formatScore(data.average) : "–"}
          </span>
          <span className="-ml-2 flex items-center bg-theme-surface pl-5 pr-3 font-display text-sm font-semibold text-theme-text-secondary">
            out of 10
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
            <FontAwesomeIcon icon={faUsers} className="w-4" />
            <span className="tabular-nums">
              {data.total} {data.total === 1 ? "rating" : "ratings"}
            </span>
          </div>
          {data.average !== null && (
            <div className="flex gap-0.5" aria-hidden="true">
              {Array.from({ length: 10 }, (_, i) => (
                <div
                  key={i}
                  className={`h-2 w-4 md:w-5 ${
                    i < Math.round(data.average) ? "bg-signal" : "bg-theme-surface"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        {session ? (
          <div className="ml-auto">
            {submitted && !showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="inline-flex h-10 items-center gap-2 bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text transition-colors duration-150 hover:text-signal dark:hover:text-[#8DB4FF]"
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit my rating
              </button>
            ) : !showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="btn-signal"
              >
                Rate this game
              </button>
            ) : null}
          </div>
        ) : (
          <p className="m-0 ml-auto text-sm text-theme-text-secondary">
            Sign in to rate this game
          </p>
        )}
      </div>

      {/* Submit form */}
      {showForm && session && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 flex flex-col gap-4 rounded-[4px] bg-theme-card p-4 md:p-6"
        >
          <p className="m-0 text-sm font-medium text-theme-text">
            {submitted ? "Update your rating" : "Your rating"}
          </p>
          <ScorePicker value={score} onChange={setScore} />
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-theme-text">Comment</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Leave a comment (optional)..."
              rows={3}
              className="w-full resize-none rounded-[3px] border border-theme-border bg-theme-bg px-4 py-3 text-[15px] text-theme-text placeholder-theme-text-secondary transition-colors focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/30"
            />
          </label>
          {error && <p className="m-0 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-signal"
            >
              {submitting ? "Submitting…" : submitted ? "Update" : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(""); }}
              className="inline-flex h-10 items-center bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text-secondary transition-colors duration-150 hover:text-theme-text"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="w-9 h-9 rounded-full bg-theme-surface flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 bg-theme-surface" />
                <div className="h-3 w-2/3 bg-theme-surface" />
              </div>
            </div>
          ))}
        </div>
      ) : data.ratings.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-[4px] bg-theme-card px-6 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center bg-theme-surface">
            <FontAwesomeIcon icon={faStar} className="h-5 w-5 text-theme-text-secondary" />
          </div>
          <div>
            <p className="m-0 font-semibold text-theme-text">No ratings yet</p>
            <p className="m-0 mt-1 text-sm text-theme-text-secondary">
              Be the first to share your score for this game
            </p>
          </div>
          {session && !showForm ? (
            <div className="flex flex-col items-center gap-3">
              <p className="m-0 text-sm text-theme-text-secondary">Pick your score to get started</p>
              <ScorePicker
                value={score}
                onChange={(v) => {
                  setScore(v);
                  setShowForm(true);
                }}
              />
            </div>
          ) : !session ? (
            <p className="m-0 font-display text-sm font-semibold text-signal dark:text-[#8DB4FF]">Sign in to rate this game</p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col">
          {data.ratings.map((r) => (
            <div
              key={r._id}
              className="flex gap-3 border-b border-theme-border py-4 last:border-0"
            >
              <div className="relative w-9 h-9 flex-shrink-0">
                <Image
                  src={r.userId?.pictureProfile || AVATAR_FALLBACK}
                  alt={r.userId?.displayName || "User"}
                  fill
                  sizes="36px"
                  className="rounded-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-theme-text">
                    {r.userId?.displayName || "Anonymous"}
                  </span>
                  <span className="score-bug h-7 min-w-[2.25rem] text-base !bg-theme-surface !text-theme-text">
                    {r.score}
                    <span className="sr-only"> out of 10</span>
                  </span>
                  <span className="ml-auto text-xs tabular-nums text-theme-text-secondary">
                    <DateFormat dateTime={r.createdAt} />
                  </span>
                </div>
                {r.comment && (
                  <p className="m-0 mt-1.5 text-[15px] leading-6 text-theme-text-secondary">
                    {r.comment}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityRating;
